const DEFAULT_PMS_BASE =
  process.env.REACT_APP_PMS_API_BASE_URL;
const ANGEL_ONE_API_BASE = "https://apiconnect.angelone.in";

const ORDER_REF_MIN_LEN = 8;
const ORDER_REF_MAX_LEN = 20;
const ORDER_REF_MAX_HYPHENS = 2;

/**
 * Groww / PMS: order_reference_id must be 8–20 chars, [A-Za-z0-9-], at most two hyphens.
 */
export function isValidOrderReferenceId(id) {
  if (id == null || typeof id !== "string") return false;
  const s = id.trim();
  if (s.length < ORDER_REF_MIN_LEN || s.length > ORDER_REF_MAX_LEN) return false;
  const hyphens = (s.match(/-/g) || []).length;
  if (hyphens > ORDER_REF_MAX_HYPHENS) return false;
  return /^[A-Za-z0-9-]+$/.test(s);
}

function randomAlnum(length) {
  const chars =
    "0123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

/**
 * Generates a compliant id (default: 12 alnum + 2 hyphens = 14 chars).
 */
export function generateOrderReferenceId() {
  return `${randomAlnum(4)}-${randomAlnum(4)}-${randomAlnum(4)}`;
}

/**
 * Returns a valid id: uses input when valid, otherwise sanitizes or generates.
 */
export function normalizeOrderReferenceId(id) {
  if (id == null) return generateOrderReferenceId();
  const s = String(id).trim();
  if (s === "") return generateOrderReferenceId();
  if (isValidOrderReferenceId(s)) return s;

  let cleaned = s.replace(/[^A-Za-z0-9-]/g, "");
  const parts = cleaned.split("-").filter((p) => p.length > 0);
  if (parts.length > ORDER_REF_MAX_HYPHENS + 1) {
    cleaned = parts.slice(0, ORDER_REF_MAX_HYPHENS + 1).join("-");
  } else {
    cleaned = parts.join("-");
  }

  if (cleaned.length > ORDER_REF_MAX_LEN) {
    cleaned = cleaned.slice(0, ORDER_REF_MAX_LEN);
  }

  if (isValidOrderReferenceId(cleaned)) return cleaned;
  return generateOrderReferenceId();
}

/**
 * Resolves broker flag for PMS APIs (login / place_order use the same values).
 */
export function getBrokerFlag(sharedData) {
  const raw = String(
    sharedData?.broker_type || localStorage.getItem("broker_type") || "5paisa",
  ).toLowerCase();
  if (raw === "grow" || raw === "3") return "grow";
  if (raw === "angelone" || raw === "4") return "angelone";
  return "5paisa";
}

export function isFivePaisaBroker(sharedData) {
  return getBrokerFlag(sharedData) === "5paisa";
}

export function isAngelOneBroker(sharedData) {
  return getBrokerFlag(sharedData) === "angelone";
}

function resolveAngelOneEdisErrorInfo(payload) {
  const root = payload && typeof payload === "object" ? payload : {};
  const dataNode = root?.data && typeof root.data === "object" ? root.data : null;
  const errorsNode =
    dataNode?.errors && typeof dataNode.errors === "object" ? dataNode.errors : null;
  const providerNode =
    errorsNode?.provider_response && typeof errorsNode.provider_response === "object"
      ? errorsNode.provider_response
      : null;
  const orderNode =
    dataNode?.order && typeof dataNode.order === "object" ? dataNode.order : null;
  const orderDataNode =
    orderNode?.data && typeof orderNode.data === "object" ? orderNode.data : null;

  const errorcode = String(
    providerNode?.errorcode ||
      errorsNode?.errorcode ||
      orderNode?.errorcode ||
      dataNode?.errorcode ||
      root?.errorcode ||
      "",
  )
    .trim()
    .toUpperCase();

  const message = String(
    providerNode?.message ||
      errorsNode?.message ||
      orderNode?.message ||
      dataNode?.message ||
      root?.message ||
      "",
  ).trim();

  const reqId = String(
    providerNode?.data?.ReqId ||
      providerNode?.ReqId ||
      errorsNode?.ReqId ||
      dataNode?.ReqId ||
      root?.ReqId ||
      "",
  ).trim();

  const uniqueOrderId = String(
    providerNode?.data?.uniqueorderid ||
      providerNode?.data?.uniqueOrderId ||
      orderDataNode?.uniqueorderid ||
      orderDataNode?.uniqueOrderId ||
      "",
  ).trim();

  return { errorcode, message, reqId, uniqueOrderId };
}

function resolveAngelOneAuthHeaders(sharedData) {
  const bearer = String(
    sharedData?.jwtToken ||
      sharedData?.accessToken ||
      sharedData?.AccessToken ||
      "",
  ).trim();
  const apiKey = String(
    sharedData?.api_key || sharedData?.apiKey || sharedData?.apikey || "",
  ).trim();
  const clientCode = String(
    sharedData?.ClientCode || sharedData?.clientcode || sharedData?.clientCode || "",
  ).trim();
  const userId = String(sharedData?.ID || sharedData?.id || "").trim();
  const localIp = String(
    sharedData?.local_ip ||
      sharedData?.localIp ||
      sharedData?.client_local_ip ||
      "127.0.0.1",
  ).trim();
  const publicIp = String(
    sharedData?.public_ip ||
      sharedData?.publicIp ||
      sharedData?.client_public_ip ||
      "127.0.0.1",
  ).trim();
  const macAddress = String(
    sharedData?.mac_address ||
      sharedData?.macAddress ||
      sharedData?.mac ||
      "00:00:00:00:00:00",
  ).trim();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-UserType": "USER",
    "X-SourceID": "WEB",
    "X-ClientLocalIP": localIp,
    "X-ClientPublicIP": publicIp,
    "X-MACAddress": macAddress,
    // Some Angel gateway setups validate this legacy casing.
    "X-MACaddress": macAddress,
  };

  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  if (apiKey) headers["X-PrivateKey"] = apiKey;
  if (clientCode) headers["X-ClientCode"] = clientCode;
  if (userId) headers["X-UserID"] = userId;

  return headers;
}

async function postAngelOneEdis({ endpoint, sharedData, body }) {
  const response = await fetch(`${ANGEL_ONE_API_BASE}${endpoint}`, {
    method: "POST",
    headers: resolveAngelOneAuthHeaders(sharedData),
    body: JSON.stringify(body || {}),
  });

  const text = await response.text();
  const payload = parseResponseText(text);
  return { ok: response.ok, status: response.status, payload };
}

function getWatchlistIsin(watchlist) {
  return String(
    watchlist?.ISIN ||
      watchlist?.isin ||
      watchlist?.Isin ||
      watchlist?.isin_code ||
      watchlist?.ISINCode ||
      "",
  ).trim();
}

function launchCdslVerification({ reqId, dpId, transDtls, version }) {
  if (typeof document === "undefined") return false;
  const form = document.createElement("form");
  form.method = "POST";
  form.action = "https://edis.cdslindia.com/eDIS/VerifyDIS/";
  form.target = "_blank";
  form.style.display = "none";

  const fields = {
    DPId: String(dpId || ""),
    ReqId: String(reqId || ""),
    Version: String(version || "1.1"),
    TransDtls: String(transDtls || ""),
  };

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
  return true;
}

function showAngelOneEdisPromptModal(message) {
  if (typeof document === "undefined") {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const backdrop = document.createElement("div");
    backdrop.style.position = "fixed";
    backdrop.style.inset = "0";
    backdrop.style.background = "rgba(15, 23, 42, 0.55)";
    backdrop.style.zIndex = "99999";
    backdrop.style.display = "flex";
    backdrop.style.alignItems = "center";
    backdrop.style.justifyContent = "center";
    backdrop.style.padding = "16px";

    const modal = document.createElement("div");
    modal.style.width = "100%";
    modal.style.maxWidth = "460px";
    modal.style.background = "#ffffff";
    modal.style.borderRadius = "12px";
    modal.style.boxShadow = "0 20px 45px rgba(2, 6, 23, 0.28)";
    modal.style.padding = "18px";
    modal.style.fontFamily = "inherit";

    const title = document.createElement("h5");
    title.textContent = "Angel One CDSL Authorization";
    title.style.margin = "0 0 10px 0";
    title.style.fontSize = "18px";
    title.style.fontWeight = "700";
    title.style.color = "#111827";

    const body = document.createElement("p");
    body.textContent =
      message ||
      "Angel One requires CDSL authorization before selling your holdings.";
    body.style.margin = "0";
    body.style.whiteSpace = "pre-line";
    body.style.fontSize = "14px";
    body.style.lineHeight = "1.45";
    body.style.color = "#374151";

    const footer = document.createElement("div");
    footer.style.display = "flex";
    footer.style.justifyContent = "flex-end";
    footer.style.gap = "10px";
    footer.style.marginTop = "16px";

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.border = "1px solid #d1d5db";
    cancelBtn.style.background = "#ffffff";
    cancelBtn.style.color = "#111827";
    cancelBtn.style.borderRadius = "8px";
    cancelBtn.style.padding = "8px 14px";
    cancelBtn.style.cursor = "pointer";

    const continueBtn = document.createElement("button");
    continueBtn.type = "button";
    continueBtn.textContent = "Continue";
    continueBtn.style.border = "none";
    continueBtn.style.background = "#16a34a";
    continueBtn.style.color = "#ffffff";
    continueBtn.style.borderRadius = "8px";
    continueBtn.style.padding = "8px 14px";
    continueBtn.style.cursor = "pointer";

    const cleanup = (value) => {
      if (backdrop.parentNode) {
        backdrop.parentNode.removeChild(backdrop);
      }
      resolve(value);
    };

    cancelBtn.addEventListener("click", () => cleanup(false));
    continueBtn.addEventListener("click", () => cleanup(true));
    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) cleanup(false);
    });

    footer.appendChild(cancelBtn);
    footer.appendChild(continueBtn);
    modal.appendChild(title);
    modal.appendChild(body);
    modal.appendChild(footer);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
  });
}

async function pollAngelOneTranStatus({ sharedData, reqId, maxAttempts = 24 }) {
  const normalizedReqId = String(reqId || "").trim();
  if (!normalizedReqId) {
    return { authorized: false, status: "0", message: "Missing ReqId for EDIS status check." };
  }

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const res = await postAngelOneEdis({
      endpoint: "/rest/secure/angelbroking/edis/v1/getTranStatus",
      sharedData,
      body: { ReqId: normalizedReqId },
    });

    const record = res?.payload?.data?.TransResDtls?.RecordResDtls?.[0] || null;
    const status = String(record?.Status ?? "");
    if (status === "1") {
      return { authorized: true, status, message: "Authorization successful." };
    }

    if (status && status !== "0") {
      return { authorized: false, status, message: "Unexpected EDIS status received." };
    }

    await new Promise((resolve) => setTimeout(resolve, 5000));
  }

  return {
    authorized: false,
    status: "0",
    message: "CDSL authorization is still pending. Please complete authorization and try again.",
  };
}

async function runAngelOneEdisFlow({
  sharedData,
  watchlist,
  qty,
  onPrompt,
  onInfo,
  onPending,
}) {
  const isin = getWatchlistIsin(watchlist);
  const quantity = Number(qty) || 0;

  if (!isin || quantity <= 0) {
    return {
      ok: false,
      authorized: false,
      message: "Missing ISIN or quantity for Angel One EDIS authorization.",
    };
  }

  const shouldContinue =
    typeof onPrompt === "function"
      ? await onPrompt(
          "Angel One requires CDSL authorization before selling your holdings.\n\nClick Continue to authorize.",
        )
      : await showAngelOneEdisPromptModal(
          "Angel One requires CDSL authorization before selling your holdings.\n\nClick Continue to authorize.",
        );

  if (!shouldContinue) {
    return { ok: false, authorized: false, message: "Authorization cancelled by user." };
  }

  const verifyRes = await postAngelOneEdis({
    endpoint: "/rest/secure/angelbroking/edis/v1/verifyDis",
    sharedData,
    body: { isin, quantity: String(quantity) },
  });

  const verifyPayload = verifyRes?.payload || {};
  const verifyErrorcode = String(verifyPayload?.errorcode || "").trim().toUpperCase();
  const verifyMessage = String(verifyPayload?.message || "").trim();
  const verifyData = verifyPayload?.data || {};
  let reqId = String(verifyData?.ReqId || "").trim();

  if (verifyPayload?.status === false && verifyErrorcode === "AG1000") {
    if (!reqId) {
      return {
        ok: false,
        authorized: false,
        message: verifyMessage || "CDSL already authorized today. Retry sell order.",
      };
    }
    return pollAngelOneTranStatus({ sharedData, reqId });
  }

  if (!verifyRes.ok || verifyPayload?.status !== true) {
    return {
      ok: false,
      authorized: false,
      message: verifyMessage || "Failed to start CDSL authorization.",
    };
  }

  reqId = String(verifyData?.ReqId || "").trim();
  const launched = launchCdslVerification({
    reqId,
    dpId: verifyData?.DPId,
    transDtls: verifyData?.TransDtls,
    version: verifyData?.version,
  });

  if (!launched) {
    return {
      ok: false,
      authorized: false,
      message: "Unable to open CDSL authorization window.",
    };
  }

  if (typeof onInfo === "function") {
    onInfo("Complete TPIN/OTP on CDSL page. We will retry automatically once authorized.");
  }

  const status = await pollAngelOneTranStatus({ sharedData, reqId });
  if (!status.authorized && typeof onPending === "function") {
    onPending(status.message);
  }
  return status;
}

function shouldHandleAngelOneEdis({ sharedData, transactionSide, payload }) {
  if (!isAngelOneBroker(sharedData)) return false;
  if (String(transactionSide || "").toLowerCase() !== "sell") return false;
  const info = resolveAngelOneEdisErrorInfo(payload);
  return info.errorcode === "AB1007";
}

const ANGEL_ONE_MODIFY_VARIETIES = new Set(["NORMAL", "STOPLOSS", "ROBO"]);

/** Angel modify_order accepts only NORMAL | STOPLOSS | ROBO (not AMO, etc.). */
export function normalizeAngelOneVariety(value) {
  const raw = String(value ?? "").trim().toUpperCase();
  if (ANGEL_ONE_MODIFY_VARIETIES.has(raw)) return raw;
  if (raw === "SL" || raw === "STOP LOSS") return "STOPLOSS";
  return "NORMAL";
}

/**
 * Scrip code resolution aligned with legacy Portfolio / Watchlist / Dashboard flows.
 */
export function resolveScripCodeForOrder(watchlist, listStatus) {
  if (!watchlist) return "";
  if (listStatus === "Portfolio" || listStatus === "DashboardPortfolio") {
    return Number(watchlist.ScripCode) || Number(watchlist.scripcode) || "";
  }
  return watchlist.Scripcode ?? "";
}

function growTradingSymbol(watchlist) {
  const exchRaw = String(watchlist?.Exch || "").toUpperCase();
  const exch = exchRaw.startsWith("B") ? "B" : exchRaw.startsWith("N") ? "N" : watchlist?.Exch;
  const nse = watchlist?.NseCode || watchlist?.nseCode;
  const bse = watchlist?.BseCode || watchlist?.bseCode;
  const sym =
    (exch === "B" ? bse : nse) ||
    watchlist?.trading_symbol ||
    watchlist?.TradingSymbol ||
    watchlist?.Symbol ||
    watchlist?.symbol ||
    watchlist?.ScripName ||
    watchlist?.scripName ||
    watchlist?.name ||
    "";
  return String(sym).trim();
}

function growExchange(exch) {
  return exch === "B" ? "BSE" : "NSE";
}

function growSegment(exch) {
  return exch === "M" ? "FNO" : "CASH";
}

function angelOneExchange(watchlist) {
  const seg = String(watchlist?.ExchSeg || watchlist?.exch_seg || "").toUpperCase();
  const exch = String(watchlist?.Exch || watchlist?.exch || "").toUpperCase();
  if (exch === "B" || seg.startsWith("BSE")) return "BSE";
  if (exch === "M" || seg.includes("MCX")) return "MCX";
  return "NSE";
}

function angelOneTradingSymbol(watchlist) {
  return String(
    watchlist?.tradingsymbol ||
      watchlist?.TradingSymbol ||
      watchlist?.Symbol ||
      watchlist?.symbol ||
      watchlist?.Name ||
      watchlist?.name ||
      "",
  ).trim();
}

function angelOneSymbolToken(watchlist) {
  return String(
    watchlist?.symboltoken ||
      watchlist?.symbol_token ||
      watchlist?.token ||
      watchlist?.Token ||
      watchlist?.ScripCode ||
      watchlist?.scripcode ||
      "",
  ).trim();
}

function buildAngelOnePmsPayload({
  email,
  watchlist,
  transactionSide,
  executionType,
  qty,
  limitPrice,
}) {
  const isMarket = executionType === "Market Order";
  const tradingsymbol = angelOneTradingSymbol(watchlist);
  const symboltoken = angelOneSymbolToken(watchlist);
  if (!tradingsymbol) {
    throw new Error("Missing tradingsymbol for AngelOne order");
  }
  if (!symboltoken) {
    throw new Error("Missing symboltoken for AngelOne order");
  }
  return {
    broker_flag: "angelone",
    email,
    tradingsymbol,
    exchange: angelOneExchange(watchlist),
    symboltoken,
    OrderType: String(transactionSide || "Buy"),
    Qty: Number(qty) || 0,
    Price: isMarket ? 0 : Number(limitPrice) || 0,
    IsIntraday: false,
    // Required by Angel One for surveillance/cautionary listed cash equity scrips (AB4036).
    scripconsent: "yes",
  };
}

function buildFivePaisaPmsPayload({
  email,
  watchlist,
  listStatus,
  transactionSide,
  executionType,
  qty,
  limitPrice,
}) {
  const exch = watchlist.Exch;
  const rawExchType = String(watchlist?.ExchType || watchlist?.exchType || "").toUpperCase();
  const exchangeType = rawExchType.startsWith("D")
    ? "D"
    : rawExchType.startsWith("C")
      ? "C"
      : exch === "M"
        ? "D"
        : "C";
  const scripCode = resolveScripCodeForOrder(watchlist, listStatus);
  const isMarket = executionType === "Market Order";
  const priceNum = isMarket ? 0 : Number(limitPrice) || 0;
  return {
    email,
    broker_flag: "5paisa",
    Exchange: exch,
    ExchangeType: exchangeType,
    ScripCode: String(scripCode),
    Price: priceNum,
    StopLossPrice: 0,
    OrderType: transactionSide,
    Qty: Number(qty) || 0,
    DisQty: "0",
    IsIntraday: false,
    iOrderValidity: "0",
    AHPlaced: "N",
  };
}

function buildGrowwPmsPayload({
  email,
  watchlist,
  transactionSide,
  executionType,
  qty,
  limitPrice,
  orderReferenceId,
}) {
  const isMarket = executionType === "Market Order";
  return {
    email,
    broker_flag: "grow",
    trading_symbol: growTradingSymbol(watchlist).toUpperCase(),
    quantity: Number(qty) || 0,
    price: isMarket ? 0 : Number(limitPrice) || 0,
    trigger_price: 0,
    validity: "DAY",
    exchange: growExchange(watchlist.Exch),
    segment: growSegment(watchlist.Exch),
    product: "CNC",
    order_type: isMarket ? "MARKET" : "LIMIT",
    transaction_type: String(transactionSide || "").toUpperCase(),
    order_reference_id: normalizeOrderReferenceId(orderReferenceId),
  };
}

/**
 * Builds the JSON body for POST /api/v1/place_order (broker-specific shape).
 */
export function buildPlaceOrderRequestBody({
  sharedData,
  watchlist,
  listStatus,
  transactionSide,
  executionType,
  qty,
  limitPrice,
  orderReferenceId,
}) {
  const email = sharedData?.Email_ID || sharedData?.email;
  if (!email) {
    throw new Error("Missing user email on sharedData");
  }
  const flag = getBrokerFlag(sharedData);
  if (flag === "grow") {
    return buildGrowwPmsPayload({
      email,
      watchlist,
      transactionSide,
      executionType,
      qty,
      limitPrice,
      orderReferenceId,
    });
  }
  if (flag === "angelone") {
    return buildAngelOnePmsPayload({
      email,
      watchlist,
      transactionSide,
      executionType,
      qty,
      limitPrice,
    });
  }
  return buildFivePaisaPmsPayload({
    email,
    watchlist,
    listStatus,
    transactionSide,
    executionType,
    qty,
    limitPrice,
  });
}

/**
 * POST place order via PMS (single entry point for all brokers).
 */
export async function placeOrderViaPms({
  pmsApiBaseUrl = DEFAULT_PMS_BASE,
  sharedData,
  watchlist,
  listStatus,
  transactionSide,
  executionType,
  qty,
  limitPrice,
  orderReferenceId,
  angelEdisFlow,
}) {
  let requestBody;
  try {
    requestBody = buildPlaceOrderRequestBody({
      sharedData,
      watchlist,
      listStatus,
      transactionSide,
      executionType,
      qty,
      limitPrice,
      orderReferenceId,
    });
  } catch (e) {
    return {
      ok: false,
      status: 0,
      data: null,
      error: e.message,
      requestBody: null,
    };
  }

  const url = `${String(pmsApiBaseUrl).replace(/\/$/, "")}/api/v1/place_order`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });
    const text = await res.text();
    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }
    }
    const response = {
      ok: res.ok,
      status: res.status,
      data,
      error: null,
      requestBody,
    };

    const edisEnabled = angelEdisFlow?.enabled !== false;
    if (
      edisEnabled &&
      shouldHandleAngelOneEdis({ sharedData, transactionSide, payload: data })
    ) {
      const edisResult = await runAngelOneEdisFlow({
        sharedData,
        watchlist,
        qty,
        onPrompt: angelEdisFlow?.onPrompt,
        onInfo: angelEdisFlow?.onInfo,
        onPending: angelEdisFlow?.onPending,
      });

      if (edisResult.authorized) {
        return placeOrderViaPms({
          pmsApiBaseUrl,
          sharedData,
          watchlist,
          listStatus,
          transactionSide,
          executionType,
          qty,
          limitPrice,
          orderReferenceId,
          angelEdisFlow: { ...angelEdisFlow, enabled: false },
        });
      }

      const info = resolveAngelOneEdisErrorInfo(data);
      return {
        ...response,
        data: {
          ...(data && typeof data === "object" ? data : {}),
          message:
            edisResult.message ||
            info.message ||
            "CDSL authorization is pending. Please complete it and retry.",
        },
      };
    }

    return response;
  } catch (e) {
    return {
      ok: false,
      status: 0,
      data: null,
      error: e.message || "Network error",
      requestBody,
    };
  }
}

function parseResponseText(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

async function postPmsJson({ pmsApiBaseUrl = DEFAULT_PMS_BASE, endpoint, requestBody }) {
  const url = `${String(pmsApiBaseUrl).replace(/\/$/, "")}${endpoint}`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });
    const text = await res.text();
    return {
      ok: res.ok,
      status: res.status,
      data: parseResponseText(text),
      error: null,
      requestBody,
    };
  } catch (e) {
    return {
      ok: false,
      status: 0,
      data: null,
      error: e.message || "Network error",
      requestBody,
    };
  }
}

function buildFivePaisaModifyOrderPayload({ email, price, stopLossPrice, exchOrderID }) {
  return {
    email,
    broker_flag: "5paisa",
    Price: Number(price) || 0,
    StopLossPrice: Number(stopLossPrice) || 0,
    ExchOrderID: String(exchOrderID || ""),
  };
}

function buildGrowwModifyOrderPayload({
  email,
  quantity,
  price,
  triggerPrice,
  orderType,
  segment,
  growwOrderId,
}) {
  return {
    email,
    broker_flag: "grow",
    quantity: Number(quantity) || 0,
    price: Number(price) || 0,
    trigger_price: Number(triggerPrice) || 0,
    order_type: String(orderType || "").toUpperCase(),
    segment: String(segment || "CASH").toUpperCase(),
    groww_order_id: String(growwOrderId || ""),
  };
}

function buildAngelOneModifyOrderPayload({
  email,
  order,
  quantity,
  price,
  orderType,
  angelOrderId,
  variety = "NORMAL",
  producttype,
  duration = "DAY",
}) {
  const watchlist = order || {};
  const tradingsymbol = angelOneTradingSymbol(watchlist);
  const symboltoken = angelOneSymbolToken(watchlist);
  const orderid = String(
    angelOrderId ||
      watchlist?.orderid ||
      watchlist?.BrokerOrderId ||
      watchlist?.broker_order_id ||
      "",
  ).trim();

  if (!orderid) {
    throw new Error("Missing orderid for AngelOne modify");
  }
  if (!tradingsymbol) {
    throw new Error("Missing tradingsymbol for AngelOne modify");
  }
  if (!symboltoken) {
    throw new Error("Missing symboltoken for AngelOne modify");
  }

  const ordertype = String(orderType || watchlist?.order_type || "LIMIT").toUpperCase();
  const isMarket = ordertype === "MARKET";
  const resolvedProductType = String(
    producttype || watchlist?.producttype || watchlist?.productType || "DELIVERY",
  ).toUpperCase();

  return {
    broker_flag: "angelone",
    email,
    orderid,
    variety: normalizeAngelOneVariety(variety || watchlist?.variety),
    ordertype,
    producttype: resolvedProductType,
    duration: String(duration || watchlist?.duration || "DAY").toUpperCase(),
    price: String(isMarket ? 0 : Number(price) || 0),
    quantity: String(Number(quantity) || 0),
    tradingsymbol,
    symboltoken,
    exchange: angelOneExchange(watchlist),
  };
}

/**
 * Builds the JSON body for POST /api/v1/modify_order (broker-specific shape).
 */
export function buildModifyOrderRequestBody({
  sharedData,
  quantity,
  price,
  triggerPrice = 0,
  orderType = "LIMIT",
  segment = "CASH",
  growwOrderId,
  exchOrderID,
  stopLossPrice = 0,
  watchlist,
  angelOrderId,
  variety,
  producttype,
  duration,
}) {
  const email = sharedData?.Email_ID || sharedData?.email;
  if (!email) {
    throw new Error("Missing user email on sharedData");
  }

  const flag = getBrokerFlag(sharedData);
  if (flag === "grow") {
    return buildGrowwModifyOrderPayload({
      email,
      quantity,
      price,
      triggerPrice,
      orderType,
      segment,
      growwOrderId,
    });
  }
  if (flag === "angelone") {
    return buildAngelOneModifyOrderPayload({
      email,
      order: watchlist,
      quantity,
      price,
      orderType,
      angelOrderId,
      variety,
      producttype,
      duration,
    });
  }

  return buildFivePaisaModifyOrderPayload({
    email,
    price,
    stopLossPrice,
    exchOrderID,
  });
}

/**
 * POST modify order via PMS (single entry point for all brokers).
 */
export async function modifyOrderViaPms({
  pmsApiBaseUrl = DEFAULT_PMS_BASE,
  sharedData,
  quantity,
  price,
  triggerPrice,
  orderType,
  segment,
  growwOrderId,
  exchOrderID,
  stopLossPrice,
  watchlist,
  angelOrderId,
  variety,
  producttype,
  duration,
}) {
  let requestBody;
  try {
    requestBody = buildModifyOrderRequestBody({
      sharedData,
      quantity,
      price,
      triggerPrice,
      orderType,
      segment,
      growwOrderId,
      exchOrderID,
      stopLossPrice,
      watchlist,
      angelOrderId,
      variety,
      producttype,
      duration,
    });
  } catch (e) {
    return {
      ok: false,
      status: 0,
      data: null,
      error: e.message,
      requestBody: null,
    };
  }

  return postPmsJson({
    pmsApiBaseUrl,
    endpoint: "/api/v1/modify_order",
    requestBody,
  });
}

function buildGrowwCancelOrderPayload({ email, segment, growwOrderId }) {
  return {
    email,
    broker_flag: "grow",
    segment: String(segment || "CASH").toUpperCase(),
    groww_order_id: String(growwOrderId || ""),
  };
}

function buildFivePaisaCancelOrderPayload({ email, exchOrderID }) {
  return {
    email,
    broker_flag: "5paisa",
    ExchOrderID: String(exchOrderID || ""),
  };
}

function buildAngelOneCancelOrderPayload({ email, angelOrderId, variety, order }) {
  const orderid = String(
    angelOrderId ||
      order?.orderid ||
      order?.BrokerOrderId ||
      order?.broker_order_id ||
      "",
  ).trim();

  if (!orderid) {
    throw new Error("Missing orderid for AngelOne cancel");
  }

  return {
    broker_flag: "angelone",
    email,
    orderid,
    variety: normalizeAngelOneVariety(variety || order?.variety),
  };
}

/**
 * Builds the JSON body for POST /api/v1/cancel_order (broker-specific shape).
 */
export function buildCancelOrderRequestBody({
  sharedData,
  segment = "CASH",
  growwOrderId,
  exchOrderID,
  angelOrderId,
  variety,
  order,
}) {
  const email = sharedData?.Email_ID || sharedData?.email;
  if (!email) {
    throw new Error("Missing user email on sharedData");
  }

  const flag = getBrokerFlag(sharedData);
  if (flag === "grow") {
    return buildGrowwCancelOrderPayload({
      email,
      segment,
      growwOrderId,
    });
  }
  if (flag === "angelone") {
    return buildAngelOneCancelOrderPayload({
      email,
      angelOrderId,
      variety,
      order,
    });
  }

  return buildFivePaisaCancelOrderPayload({
    email,
    exchOrderID,
  });
}

/**
 * POST cancel order via PMS (single entry point for all brokers).
 */
export async function cancelOrderViaPms({
  pmsApiBaseUrl = DEFAULT_PMS_BASE,
  sharedData,
  segment,
  growwOrderId,
  exchOrderID,
  angelOrderId,
  variety,
  order,
}) {
  let requestBody;
  try {
    requestBody = buildCancelOrderRequestBody({
      sharedData,
      segment,
      growwOrderId,
      exchOrderID,
      angelOrderId,
      variety,
      order,
    });
  } catch (e) {
    return {
      ok: false,
      status: 0,
      data: null,
      error: e.message,
      requestBody: null,
    };
  }

  return postPmsJson({
    pmsApiBaseUrl,
    endpoint: "/api/v1/cancel_order",
    requestBody,
  });
}

/**
 * Maps either legacy 5paisa OpenAPI JSON or PMS wrapper JSON into one shape for UI handlers.
 */
export function normalizePlaceOrderResponse(data) {
  if (!data || typeof data !== "object") {
    return {
      message: "",
      rmsResponseCode: undefined,
      brokerOrderID: "",
      exchangeOrderID: "",
      headStatus: "1",
      isOpenApiShape: false,
      raw: data,
    };
  }

  if (data.body !== undefined && (data.head !== undefined || data.body.Message !== undefined)) {
    return {
      message: data.body?.Message || "",
      rmsResponseCode: data.body?.RMSResponseCode,
      brokerOrderID: data.body?.BrokerOrderID || "",
      exchangeOrderID: data.body?.ExchOrderID || "",
      headStatus: String(data.head?.status ?? ""),
      isOpenApiShape: true,
      raw: data,
    };
  }

  const inner = data.data !== undefined ? data.data : data;

  const ord =
    inner?.order && typeof inner.order === "object" ? inner.order : null;

  let brokerOrderID =
    inner.broker_order_id ??
    inner.BrokerOrderID ??
    inner.brokerOrderId ??
    inner.order_id ??
    inner.orderId ??
    "";

  let exchangeOrderID =
    inner.exchange_order_id ??
    inner.ExchOrderID ??
    "";

  let message =
    inner.message ??
    inner.Message ??
    data.message ??
    (typeof inner === "string" ? inner : "");

  /** PMS Groww wraps broker outcome in data.order (HTTP + api_status may still look "success"). */
  const growwFailureStatuses = new Set([
    "FAILED",
    "REJECTED",
    "REJECT",
    "CANCELLED",
    "CANCELED",
    "ERROR",
    "DECLINED",
    "INVALID",
  ]);
  const growwPendingStatuses = new Set([
    "NEW",
    "OPEN",
    "PENDING",
    "TRIGGER PENDING",
    "VALIDATION PENDING",
    "PUT ORDER REQ RECEIVED",
  ]);

  let nestedBrokerOrderFailed = false;
  let nestedBrokerOrderPending = false;
  if (ord) {
    const angelInner =
      ord?.data && typeof ord.data === "object" ? ord.data : null;
    const angelOrderId = angelInner?.orderid ?? angelInner?.orderId;
    const angelUniqueId =
      angelInner?.uniqueorderid ?? angelInner?.uniqueOrderId;
    const isAngelOneOrder =
      angelOrderId != null ||
      ord.status === true ||
      ord.status === false ||
      String(ord.errorcode || "").trim() !== "";

    if (angelOrderId != null && String(angelOrderId).trim() !== "") {
      brokerOrderID = String(angelOrderId);
    }
    if (angelUniqueId != null && String(angelUniqueId).trim() !== "") {
      exchangeOrderID = String(angelUniqueId);
    }

    if (
      isAngelOneOrder &&
      (ord.status === false || String(ord.errorcode || "").trim() !== "")
    ) {
      nestedBrokerOrderFailed = true;
      message =
        String(ord.message ?? ord.Message ?? "").trim() ||
        String(message || "") ||
        "Order could not be placed";
    }

    const growwId = ord.groww_order_id ?? ord.GrowwOrderId;
    if (growwId != null && String(growwId).trim() !== "") {
      brokerOrderID = String(growwId);
    }

    const orderStatus = String(
      ord.order_status ?? ord.OrderStatus ?? "",
    ).toUpperCase();
    const statusCodeRaw = ord.Status;
    const statusCode =
      statusCodeRaw === undefined || statusCodeRaw === null
        ? undefined
        : Number(statusCodeRaw);
    const nestedRmsCodeRaw = ord.RMSResponseCode ?? ord.rms_response_code;
    const nestedRmsCode =
      nestedRmsCodeRaw === undefined || nestedRmsCodeRaw === null
        ? undefined
        : Number(nestedRmsCodeRaw);
    const nestedMsg = String(ord.Message ?? ord.message ?? "").trim();
    const nestedMsgLooksRejected =
      /rejected|not allowed|authorization|rms|denied|invalid/i.test(nestedMsg);

    if (growwFailureStatuses.has(orderStatus)) {
      nestedBrokerOrderFailed = true;
      const remark = ord.remark ?? ord.Remark ?? "";
      const outerMsg = String(message || "");
      const outerLooksLikeFalseSuccess =
        /placed successfully|order successful/i.test(outerMsg);
      message = String(
        remark ||
          (!outerLooksLikeFalseSuccess && outerMsg ? outerMsg : "") ||
          "Order could not be placed",
      );
    } else if (
      (Number.isFinite(nestedRmsCode) && nestedRmsCode < 0) ||
      (Number.isFinite(statusCode) && statusCode !== 0) ||
      nestedMsgLooksRejected
    ) {
      nestedBrokerOrderFailed = true;
      message =
        nestedMsg ||
        String(message || "") ||
        "Order could not be placed";
    } else if (
      !isAngelOneOrder &&
      (growwPendingStatuses.has(orderStatus) || !orderStatus)
    ) {
      nestedBrokerOrderPending = true;
      const remark = String(ord.remark ?? ord.Remark ?? "").trim();
      message =
        remark ||
        nestedMsg ||
        "Order request sent. Please wait and check Order page for final status.";
    }
  }

  const statusStr = String(inner.status ?? inner.Status ?? "").toLowerCase();
  const okFlag =
    statusStr === "success" ||
    inner.success === true ||
    data.success === true;

  let headStatus;
  if (nestedBrokerOrderFailed) {
    headStatus = "1";
  } else if (nestedBrokerOrderPending) {
    headStatus = "0";
  } else if (okFlag) {
    headStatus = "0";
  } else {
    headStatus = "1";
  }

  return {
    message: String(message || ""),
    rmsResponseCode: inner.rms_response_code ?? inner.RMSResponseCode,
    brokerOrderID: brokerOrderID ? String(brokerOrderID) : "",
    exchangeOrderID: String(
      exchangeOrderID ||
        inner.exchange_order_id ||
        inner.ExchOrderID ||
        ord?.exchange_order_id ||
        "",
    ),
    headStatus,
    isOpenApiShape: false,
    raw: data,
  };
}

const DEFAULT_PLACE_ORDER_ACCEPTED_TOAST =
  "Order request sent. Redirecting to Orders page to check the latest status.";

/**
 * Toast copy when place_order API accepts the request (final status is on Orders page).
 */
export function getPlaceOrderAcceptedToastMessage(tr) {
  if (typeof tr === "function") {
    const translated = tr("orderRequestSentRedirect");
    if (translated && translated !== "orderRequestSentRedirect") {
      return translated;
    }
  }
  return DEFAULT_PLACE_ORDER_ACCEPTED_TOAST;
}

/**
 * After headStatus "0": notify user, close buy/sell modals, redirect to Orders.
 */
export function runPlaceOrderAcceptedFlow({
  transactionSide,
  tr,
  showSuccessToast,
  navigate,
  redirectDelayMs = 1500,
}) {
  showSuccessToast(getPlaceOrderAcceptedToastMessage(tr));

  if (transactionSide === "Buy") {
    document.getElementById("buyModalClick")?.click();
  } else if (transactionSide === "Sell") {
    document.getElementById("sellModalClick")?.click();
  }

  setTimeout(() => {
    if (typeof navigate === "function") {
      navigate("/Order");
    }
  }, redirectDelayMs);
}
